import { useEffect, useMemo, useState } from "react";
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useContent } from "../context/SiteContext";
import { Container, PageHero } from "../components/site/Common";

const dash = (v) => (v && String(v).trim() ? v : "—");
const PAGE_SIZES = [10, 25, 50, 100];
const SORTABLE = { name: "Player Name", tnba_id: "Player ID", gender: "Gender", status: "Status" };
const COLS = ["S.No", "name", "tnba_id", "gender", "status"];
const statusOf = (p) => (p.active === false ? "Inactive" : "Active");

const StatusPill = ({ p }) => (
  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${p.active === false ? "bg-slate-100 text-slate-600 ring-slate-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}>{p.active === false ? "Inactive" : "Active"}</span>
);

const SortHeader = ({ k, sort, setSort }) => {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <button onClick={() => setSort({ key: k, dir: active && sort.dir === "asc" ? "desc" : "asc" })} className="inline-flex items-center gap-1 font-semibold hover:text-blue-200" data-testid={`players-sort-${k}`}>
      {SORTABLE[k]} <Icon className={`h-3.5 w-3.5 ${active ? "" : "opacity-50"}`} />
    </button>
  );
};

const Pager = ({ page, pages, setPage }) => (
  <div className="flex items-center gap-1">
    <button disabled={page === 1} onClick={() => setPage(page - 1)} className="p-2 rounded-lg ring-1 ring-slate-200 bg-white disabled:opacity-40" data-testid="players-prev-page" aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></button>
    {Array.from({ length: pages }, (_, i) => i + 1).filter((n) => n === 1 || n === pages || Math.abs(n - page) <= 1).map((n, i, arr) => (
      <span key={n} className="flex items-center">
        {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-slate-400">…</span>}
        <button onClick={() => setPage(n)} className={`min-w-[36px] h-9 rounded-lg text-sm font-medium ${n === page ? "bg-[#0B1F44] text-white" : "bg-white ring-1 ring-slate-200 text-slate-700"}`} data-testid={`players-page-${n}`}>{n}</button>
      </span>
    ))}
    <button disabled={page === pages} onClick={() => setPage(page + 1)} className="p-2 rounded-lg ring-1 ring-slate-200 bg-white disabled:opacity-40" data-testid="players-next-page" aria-label="Next page"><ChevronRight className="h-4 w-4" /></button>
  </div>
);

const ctl = "rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function Players() {
  const { items, error } = useContent("players");
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(1);
  const options = (k) => [...new Set((items || []).map((p) => p[k]).filter(Boolean))].sort();

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = (items || []).map((p) => ({ ...p, status: statusOf(p) })).filter((p) => (!gender || p.gender === gender) && (!status || p.status === status) &&
      (!s || [p.name, p.tnba_id].some((v) => String(v || "").toLowerCase().includes(s))));
    const dir = sort.dir === "asc" ? 1 : -1;
    return rows.sort((a, b) => {
      const x = String(a[sort.key] || ""), y = String(b[sort.key] || "");
      if (!x !== !y) return x ? -1 : 1;
      return x.localeCompare(y, undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [items, q, gender, status, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / size));
  useEffect(() => setPage(1), [q, gender, status, size, sort]);
  const start = (page - 1) * size;
  const rows = filtered.slice(start, start + size);

  return (
    <>
      <PageHero eyebrow="Player details" title="Registered players" text="Players of the Thanjavur District Badminton Association." />
      <section className="py-12 bg-slate-50 min-h-[50vh]">
        <Container>
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-5" data-testid="players-toolbar">
            <div className="relative w-full lg:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by player name or player ID" className={`${ctl} w-full pl-9`} data-testid="players-search-input" />
            </div>
            <div className="flex flex-wrap gap-3">
              <select value={gender} onChange={(e) => setGender(e.target.value)} className={ctl} data-testid="players-gender-filter">
                <option value="">All genders</option>
                {options("gender").map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={ctl} data-testid="players-status-filter">
                <option value="">All statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600">Show
                <select value={size} onChange={(e) => setSize(Number(e.target.value))} className={ctl} data-testid="players-page-size">
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>entries
              </label>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="players-table-wrapper">
            <table className="w-full min-w-[640px] text-sm text-left" data-testid="players-table">
              <thead className="bg-[#0B1F44] text-white">
                <tr>
                  {COLS.map((c) => (
                    <th key={c} scope="col" className={`px-4 py-3.5 font-semibold whitespace-nowrap ${c === "S.No" ? "w-16" : ""}`}>
                      {SORTABLE[c] ? <SortHeader k={c} sort={sort} setSort={setSort} /> : c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!items && <tr><td colSpan={COLS.length} className="px-4 py-10 text-center text-slate-500" data-testid="players-loading">Loading players...</td></tr>}
                {items && error && <tr><td colSpan={COLS.length} className="px-4 py-12 text-center text-red-600 font-medium" data-testid="players-error">Could not load players. Please refresh the page.</td></tr>}
                {items && !error && rows.length === 0 && <tr><td colSpan={COLS.length} className="px-4 py-12 text-center text-slate-500 font-medium" data-testid="players-empty">No players found</td></tr>}
                {rows.map((p, i) => (
                  <tr key={p.id} className={`${i % 2 ? "bg-blue-50/70" : "bg-white"} border-t border-slate-100 hover:bg-blue-100/60 transition-colors`} data-testid={`player-row-${p.id}`}>
                    <td className="px-4 py-3 text-slate-500">{start + i + 1}</td>
                    <td className="px-4 py-3 font-semibold text-[#0B1F44]" data-testid={`player-row-name-${p.id}`}>{p.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{dash(p.tnba_id)}</td>
                    <td className="px-4 py-3">{dash(p.gender)}</td>
                    <td className="px-4 py-3"><StatusPill p={p} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between text-sm text-slate-600">
            <p data-testid="players-showing">{filtered.length ? `Showing ${start + 1} to ${start + rows.length} of ${filtered.length} entries` : "Showing 0 entries"}</p>
            <Pager page={page} pages={pages} setPage={setPage} />
          </div>
        </Container>
      </section>
    </>
  );
}
